/**
 * The game, and nothing else. Pure: step(state, action) → { state, events }.
 * No timers, no sockets, no randomness — a shuffled deck arrives as part of the
 * authority actions (`cut-for-deal`, `deal`), so a game can be replayed from
 * (seed, actions[]). Everything the UI animates comes from the ordered events.
 */

import { type Card, rankOf, sortCards, valueOf } from './cards.js';
import { type Score, type ScoreItem, scoreHand, scorePeg } from './scoring.js';

export type Seat = 0 | 1;
export const other = (s: Seat): Seat => (s === 0 ? 1 : 0);

export const WIN = 121;
export const SKUNK = 91;
export const DOUBLE_SKUNK = 61; // house convention, not ACC

export type Phase = 'cut-for-deal' | 'discard' | 'cut-starter' | 'pegging' | 'show' | 'hand-end' | 'game-over';
export type ShowStep = 'pone' | 'dealer' | 'crib';

export interface PegState {
  stack: Card[];
  count: number;
  turn: Seat;
  go: [boolean, boolean];
  lastPlayer: Seat | null;
  /** Every card each seat has played this pegging phase (public). */
  played: [Card[], Card[]];
}

export interface GameState {
  seq: number;
  phase: Phase;
  dealer: Seat | null;
  scores: [number, number];
  handNo: number;
  /** The full ordered deck of this hand. Hidden. */
  deck: Card[];
  /** Cards still held (pegging removes from here). */
  hands: [Card[], Card[]];
  /** The four kept, for the show. */
  kept: [Card[], Card[]];
  crib: Card[];
  starter: Card | null;
  cutCards: [Card | null, Card | null];
  submitted: [boolean, boolean];
  acked: [boolean, boolean];
  /** Hand-end: both players have acked and the authority should deal. */
  dealPending: boolean;
  peg: PegState | null;
  show: { step: ShowStep; score: Score } | null;
  winner: Seat | null;
  skunk: 0 | 1 | 2;
}

export type ScoreSource = 'heels' | 'peg' | 'go' | 'lastcard' | 'show-pone' | 'show-dealer' | 'show-crib';

export type GameEvent =
  | { t: 'cut'; seat: Seat; card: Card }
  | { t: 'cut-tie' }
  | { t: 'dealer'; seat: Seat }
  | { t: 'dealt'; handNo: number; dealer: Seat }
  | { t: 'discarded'; seat: Seat }
  | { t: 'starter'; card: Card }
  | { t: 'played'; seat: Seat; card: Card; count: number }
  | { t: 'go'; seat: Seat }
  | { t: 'reset'; leader: Seat }
  | { t: 'score'; seat: Seat; source: ScoreSource; items: ScoreItem[]; from: number; to: number }
  | { t: 'show'; step: ShowStep; seat: Seat; cards: Card[]; starter: Card; score: Score }
  | { t: 'hand-end'; scores: [number, number] }
  | { t: 'game-over'; winner: Seat; skunk: 0 | 1 | 2 };

/** Actions a seat may take. The adapter stamps `seat` and `seq`. */
export type SeatActionBody =
  | { t: 'discard'; cards: [Card, Card] }
  | { t: 'cut' }
  | { t: 'play'; card: Card }
  | { t: 'go' }
  | { t: 'ack' };
export type SeatAction = SeatActionBody & { seat: Seat; seq: number };

/** Actions only the authority (local driver or server) may take. */
export type AuthorityAction =
  | { t: 'cut-for-deal'; deck: Card[] }
  | { t: 'deal'; deck: Card[] }
  | { t: 'forfeit'; seat: Seat };

export type Action = SeatAction | AuthorityAction;

export type IllegalCode =
  | 'STALE_SEQ'
  | 'WRONG_PHASE'
  | 'NOT_YOUR_TURN'
  | 'ALREADY_DONE'
  | 'NOT_IN_HAND'
  | 'OVER_31'
  | 'MUST_PLAY'
  | 'BAD_INPUT'
  | 'GAME_OVER';

export class IllegalAction extends Error {
  constructor(public code: IllegalCode) {
    super(code);
  }
}

export function newGame(): GameState {
  return {
    seq: 0,
    phase: 'cut-for-deal',
    dealer: null,
    scores: [0, 0],
    handNo: 0,
    deck: [],
    hands: [[], []],
    kept: [[], []],
    crib: [],
    starter: null,
    cutCards: [null, null],
    submitted: [false, false],
    acked: [false, false],
    dealPending: false,
    peg: null,
    show: null,
    winner: null,
    skunk: 0,
  };
}

/**
 * A fresh game where `firstDealer` deals the first hand (used for "play again":
 * the loser deals). `deal` flips the dealer, so the state is seeded with the
 * other seat and dealt from the hand-end phase.
 */
export function newGameWithDealer(firstDealer: Seat, deck: Card[]): StepResult {
  const s: GameState = { ...newGame(), dealer: other(firstDealer), phase: 'hand-end', dealPending: true };
  return step(s, { t: 'deal', deck });
}

export function pone(s: GameState): Seat {
  if (s.dealer === null) throw new Error('no dealer yet');
  return other(s.dealer);
}

/** Cards in `hand` that fit under 31 on the current count. */
export function legalPlays(hand: readonly Card[], count: number): Card[] {
  return hand.filter((c) => count + valueOf(c) <= 31);
}

function canPlay(s: GameState, seat: Seat): boolean {
  return legalPlays(s.hands[seat], s.peg!.count).length > 0;
}

function clone(s: GameState): GameState {
  return {
    ...s,
    scores: [s.scores[0], s.scores[1]],
    deck: [...s.deck],
    hands: [[...s.hands[0]], [...s.hands[1]]],
    kept: [[...s.kept[0]], [...s.kept[1]]],
    crib: [...s.crib],
    cutCards: [s.cutCards[0], s.cutCards[1]],
    submitted: [s.submitted[0], s.submitted[1]],
    acked: [s.acked[0], s.acked[1]],
    peg: s.peg
      ? {
          ...s.peg,
          stack: [...s.peg.stack],
          go: [s.peg.go[0], s.peg.go[1]],
          played: [[...s.peg.played[0]], [...s.peg.played[1]]],
        }
      : null,
    show: s.show ? { step: s.show.step, score: s.show.score } : null,
  };
}

function isFullDeck(d: unknown): d is Card[] {
  if (!Array.isArray(d) || d.length !== 52) return false;
  const seen = new Set<number>();
  for (const c of d) {
    if (typeof c !== 'number' || !Number.isInteger(c) || c < 0 || c > 51 || seen.has(c)) return false;
    seen.add(c);
  }
  return true;
}

export interface StepResult {
  state: GameState;
  events: GameEvent[];
}

export function step(prev: GameState, action: Action): StepResult {
  const events: GameEvent[] = [];
  const s = clone(prev);

  if (s.phase === 'game-over' && action.t !== 'forfeit') throw new IllegalAction('GAME_OVER');

  // Scoring helper with the win check. Returns true if the game ended.
  const score = (seat: Seat, source: ScoreSource, items: ScoreItem[]): boolean => {
    const pts = items.reduce((a, i) => a + i.points, 0);
    if (pts === 0) return false;
    const from = s.scores[seat];
    const to = Math.min(WIN, from + pts);
    s.scores[seat] = to;
    events.push({ t: 'score', seat, source, items, from, to });
    if (to >= WIN) {
      end(seat);
      return true;
    }
    return false;
  };
  const end = (winner: Seat) => {
    const loser = s.scores[other(winner)];
    s.winner = winner;
    s.skunk = loser < DOUBLE_SKUNK ? 2 : loser < SKUNK ? 1 : 0;
    s.phase = 'game-over';
    s.peg = null;
    events.push({ t: 'game-over', winner, skunk: s.skunk });
  };

  switch (action.t) {
    // ── authority ──
    case 'cut-for-deal': {
      if (s.phase !== 'cut-for-deal') throw new IllegalAction('WRONG_PHASE');
      if (!isFullDeck(action.deck)) throw new IllegalAction('BAD_INPUT');
      const a = action.deck[0]!;
      const b = action.deck[1]!;
      s.cutCards = [a, b];
      events.push({ t: 'cut', seat: 0, card: a }, { t: 'cut', seat: 1, card: b });
      if (rankOf(a) === rankOf(b)) {
        events.push({ t: 'cut-tie' });
        break;
      }
      s.dealer = rankOf(a) < rankOf(b) ? 0 : 1;
      events.push({ t: 'dealer', seat: s.dealer });
      // Deal immediately from the same deck (the cut cards go back in; the
      // deck is already a fresh shuffle, so dealing from index 2 is fair).
      deal(action.deck.slice(2).concat([a, b]));
      break;
    }
    case 'deal': {
      if (s.phase !== 'hand-end' || !s.dealPending) throw new IllegalAction('WRONG_PHASE');
      if (!isFullDeck(action.deck)) throw new IllegalAction('BAD_INPUT');
      if (s.dealer === null) throw new IllegalAction('WRONG_PHASE');
      s.dealer = other(s.dealer);
      deal(action.deck);
      break;
    }
    case 'forfeit': {
      if (s.phase === 'game-over') throw new IllegalAction('GAME_OVER');
      end(other(action.seat));
      break;
    }

    // ── seat actions ──
    case 'discard': {
      seatGuard(action);
      if (s.phase !== 'discard') throw new IllegalAction('WRONG_PHASE');
      if (s.submitted[action.seat]) throw new IllegalAction('ALREADY_DONE');
      const [a, b] = action.cards;
      if (typeof a !== 'number' || typeof b !== 'number' || a === b) throw new IllegalAction('BAD_INPUT');
      const hand = s.hands[action.seat];
      if (!hand.includes(a) || !hand.includes(b)) throw new IllegalAction('NOT_IN_HAND');
      s.hands[action.seat] = hand.filter((c) => c !== a && c !== b);
      s.kept[action.seat] = [...s.hands[action.seat]];
      s.crib.push(a, b);
      s.submitted[action.seat] = true;
      events.push({ t: 'discarded', seat: action.seat });
      if (s.submitted[0] && s.submitted[1]) s.phase = 'cut-starter';
      break;
    }
    case 'cut': {
      seatGuard(action);
      if (s.phase !== 'cut-starter') throw new IllegalAction('WRONG_PHASE');
      if (action.seat !== pone(s)) throw new IllegalAction('NOT_YOUR_TURN');
      const starter = s.deck[12]!;
      s.starter = starter;
      events.push({ t: 'starter', card: starter });
      s.phase = 'pegging';
      s.peg = { stack: [], count: 0, turn: pone(s), go: [false, false], lastPlayer: null, played: [[], []] };
      if (rankOf(starter) === 11) {
        if (score(s.dealer!, 'heels', [{ kind: 'heels', cards: [starter], points: 2 }])) break;
      }
      break;
    }
    case 'play': {
      seatGuard(action);
      if (s.phase !== 'pegging') throw new IllegalAction('WRONG_PHASE');
      const peg = s.peg!;
      if (action.seat !== peg.turn) throw new IllegalAction('NOT_YOUR_TURN');
      const hand = s.hands[action.seat];
      if (typeof action.card !== 'number' || !hand.includes(action.card)) throw new IllegalAction('NOT_IN_HAND');
      if (peg.count + valueOf(action.card) > 31) throw new IllegalAction('OVER_31');
      s.hands[action.seat] = hand.filter((c) => c !== action.card);
      peg.stack.push(action.card);
      peg.count += valueOf(action.card);
      peg.played[action.seat].push(action.card);
      peg.lastPlayer = action.seat;
      events.push({ t: 'played', seat: action.seat, card: action.card, count: peg.count });
      const sc = scorePeg(peg.stack);
      if (score(action.seat, 'peg', sc.items)) break;
      resolve(action.seat);
      break;
    }
    case 'go': {
      seatGuard(action);
      if (s.phase !== 'pegging') throw new IllegalAction('WRONG_PHASE');
      const peg = s.peg!;
      if (action.seat !== peg.turn) throw new IllegalAction('NOT_YOUR_TURN');
      if (s.hands[action.seat].length === 0) throw new IllegalAction('BAD_INPUT');
      if (canPlay(s, action.seat)) throw new IllegalAction('MUST_PLAY');
      peg.go[action.seat] = true;
      events.push({ t: 'go', seat: action.seat });
      // Resolve from the opponent's perspective: they are now "the player who
      // last acted" for the purpose of deciding who continues.
      resolveAfterGo(action.seat);
      break;
    }
    case 'ack': {
      seatGuard(action);
      if (s.phase !== 'show' && s.phase !== 'hand-end') throw new IllegalAction('WRONG_PHASE');
      if (s.acked[action.seat]) throw new IllegalAction('ALREADY_DONE');
      s.acked[action.seat] = true;
      if (s.acked[0] && s.acked[1]) {
        s.acked = [false, false];
        if (s.phase === 'show') advanceShow();
        // hand-end: both acked; the authority must now `deal`.
        else s.dealPending = true;
      }
      break;
    }
    default:
      throw new IllegalAction('BAD_INPUT');
  }

  s.seq = prev.seq + 1;
  return { state: s, events };

  // ── helpers (closures over s / events) ──

  function seatGuard(a: SeatAction) {
    if (a.seat !== 0 && a.seat !== 1) throw new IllegalAction('BAD_INPUT');
    if (a.seq !== prev.seq) throw new IllegalAction('STALE_SEQ');
  }

  function deal(deck: Card[]) {
    s.deck = deck;
    s.handNo += 1;
    const p = pone(s);
    const d = s.dealer!;
    const hp: Card[] = [];
    const hd: Card[] = [];
    for (let i = 0; i < 12; i++) (i % 2 === 0 ? hp : hd).push(deck[i]!);
    s.hands = [[], []];
    s.hands[p] = sortCards(hp);
    s.hands[d] = sortCards(hd);
    s.kept = [[], []];
    s.crib = [];
    s.starter = null;
    s.submitted = [false, false];
    s.acked = [false, false];
    s.dealPending = false;
    s.peg = null;
    s.show = null;
    s.phase = 'discard';
    events.push({ t: 'dealt', handNo: s.handNo, dealer: d });
  }

  /** After seat `p` played a card (and did not win). */
  function resolve(p: Seat) {
    const peg = s.peg!;
    const o = other(p);
    if (peg.count === 31) {
      reset();
      return;
    }
    if (s.hands[0].length === 0 && s.hands[1].length === 0) {
      if (score(peg.lastPlayer!, 'lastcard', [{ kind: 'lastcard', cards: [], points: 1 }])) return;
      startShow();
      return;
    }
    if (canPlay(s, o)) {
      peg.turn = o;
      return;
    }
    if (s.hands[o].length > 0 && !peg.go[o]) {
      // Opponent holds cards but none fits: they must say Go themselves.
      peg.turn = o;
      return;
    }
    // Opponent has said go, or is out of cards (implicit go).
    if (canPlay(s, p)) {
      peg.turn = p;
      return;
    }
    if (score(p, 'go', [{ kind: 'go', cards: [], points: 1 }])) return;
    reset();
  }

  /** After seat `g` said go. */
  function resolveAfterGo(g: Seat) {
    const peg = s.peg!;
    const o = other(g);
    if (canPlay(s, o)) {
      peg.turn = o; // o keeps playing every card that fits
      return;
    }
    // Neither can play. If o never played in this sub-count (stack empty is
    // impossible here since g could not have gone on an empty count... but
    // guard anyway), the go point goes to whoever played last.
    const last = peg.lastPlayer;
    if (last === null) {
      reset();
      return;
    }
    if (score(last, 'go', [{ kind: 'go', cards: [], points: 1 }])) return;
    if (s.hands[0].length === 0 && s.hands[1].length === 0) {
      startShow();
      return;
    }
    reset();
  }

  function reset() {
    const peg = s.peg!;
    if (s.hands[0].length === 0 && s.hands[1].length === 0) {
      startShow();
      return;
    }
    const last = peg.lastPlayer!;
    const o = other(last);
    const leader: Seat = s.hands[o].length > 0 ? o : last;
    peg.stack = [];
    peg.count = 0;
    peg.go = [false, false];
    peg.turn = leader;
    events.push({ t: 'reset', leader });
  }

  function startShow() {
    s.peg = null;
    s.phase = 'show';
    s.acked = [false, false];
    showStep('pone');
  }

  function showStep(stepName: ShowStep) {
    const d = s.dealer!;
    const p = other(d);
    const seat = stepName === 'pone' ? p : d;
    const cards = stepName === 'crib' ? s.crib : s.kept[seat];
    const sc = scoreHand(cards, s.starter!, stepName === 'crib');
    s.show = { step: stepName, score: sc };
    events.push({ t: 'show', step: stepName, seat, cards: [...cards], starter: s.starter!, score: sc });
    const source: ScoreSource = stepName === 'pone' ? 'show-pone' : stepName === 'dealer' ? 'show-dealer' : 'show-crib';
    score(seat, source, sc.items);
  }

  function advanceShow() {
    const cur = s.show!.step;
    if (cur === 'pone') showStep('dealer');
    else if (cur === 'dealer') showStep('crib');
    else {
      s.show = null;
      s.phase = 'hand-end';
      s.acked = [false, false];
      s.dealPending = false;
      events.push({ t: 'hand-end', scores: [s.scores[0], s.scores[1]] });
    }
  }
}

/** True when both seats have acked the hand-end and the authority should deal. */
export function readyToDeal(s: GameState): boolean {
  return s.phase === 'hand-end' && s.dealPending;
}

/** True when `seat` must say Go (holds cards, none fits, it is their turn). */
export function mustGo(s: GameState, seat: Seat): boolean {
  if (s.phase !== 'pegging' || !s.peg || s.peg.turn !== seat) return false;
  return s.hands[seat].length > 0 && legalPlays(s.hands[seat], s.peg.count).length === 0;
}
